/**
 * DOMI-CARE - Módulo de Calculadoras Clínicas y Escalas Estandarizadas
 * Incluye: Braden (Úlceras por presión), Barthel (AVD Dependencia) y Morse (Caídas).
 */

import { offlineStore } from '../../api/offlineStore.js';
import { appsScriptClient } from '../../api/appsScriptClient.js';
import { authState } from '../../state/authState.js';
import { notify } from '../../components/notificationCenter.js';

let currentTab = 'braden'; // 'braden', 'barthel', 'morse'

export async function renderEscalasView(container, activePatientId) {
  const paciente = await offlineStore.getById('pacientes', activePatientId);
  if (!paciente) {
    container.innerHTML = `<div class="card" style="padding: 2rem; text-align: center;">Por favor selecciona un paciente en el padrón.</div>`;
    return;
  }

  const allEscalas = await offlineStore.getAll('escalas');
  const historicoEscalas = allEscalas.filter(e => e.patientId === activePatientId)
    .sort((a, b) => new Date(b.fechaHora) - new Date(a.fechaHora));

  container.innerHTML = `
    <div class="view-header">
      <div>
        <h1 class="view-title">Escalas Clínicas y Calculadoras Geriátricas</h1>
        <p class="view-subtitle">Valoración estandarizada del riesgo de UPP, nivel de dependencia y caídas</p>
      </div>
    </div>

    <!-- Pestañas de Escalas -->
    <div style="display: flex; gap: 0.5rem; margin-bottom: 1.5rem; border-bottom: 2px solid var(--border-medium); padding-bottom: 0.5rem; overflow-x: auto;">
      <button class="btn ${currentTab === 'braden' ? 'btn-primary' : 'btn-secondary'} btn-tab" data-tab="braden">
        🛡️ Escala de Braden (Riesgo UPP)
      </button>
      <button class="btn ${currentTab === 'barthel' ? 'btn-primary' : 'btn-secondary'} btn-tab" data-tab="barthel">
        🚶 Índice de Barthel (Dependencia AVD)
      </button>
      <button class="btn ${currentTab === 'morse' ? 'btn-primary' : 'btn-secondary'} btn-tab" data-tab="morse">
        ⚠️ Escala de Morse (Riesgo de Caídas)
      </button>
    </div>

    <!-- Contenedor Activo de la Calculadora -->
    <div id="calculator-content">
      ${currentTab === 'braden' ? renderBradenCalculator() : 
        currentTab === 'barthel' ? renderBarthelCalculator() : renderMorseCalculator()}
    </div>

    <!-- Historial de Evaluaciones del Paciente -->
    <div style="margin-top: 2rem;">
      <h3 style="font-size: 1.1rem; font-weight: 800; color: var(--text-primary); margin-bottom: 0.75rem;">
        📈 Histórico de Valoraciones para ${paciente.nombreCompleto} (${historicoEscalas.length})
      </h3>
      <div class="card" style="padding: 0; overflow: hidden;">
        <table class="table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tipo de Escala</th>
              <th>Puntaje Obtenido</th>
              <th>Clasificación de Riesgo / Grado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            ${historicoEscalas.length === 0 ? `
              <tr><td colspan="5" style="text-align: center; color: var(--text-muted); padding: 1.5rem;">No se han registrado escalas aún para este paciente.</td></tr>
            ` : historicoEscalas.map(e => `
              <tr>
                <td><strong>${e.fechaHora}</strong></td>
                <td><span class="badge badge-primary">${e.tipo}</span></td>
                <td><strong style="font-size: 1.05rem; font-family: var(--font-mono);">${e.puntaje} pts</strong></td>
                <td><span class="badge" style="background: ${e.color || '#e0f2fe'}; color: #0f172a;">${e.clasificacion}</span></td>
                <td><span style="font-size: 0.8rem; color: var(--text-muted);">Registrado</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>
  `;

  // Listeners de Pestañas
  container.querySelectorAll('.btn-tab').forEach(b => {
    b.addEventListener('click', (e) => {
      currentTab = e.currentTarget.dataset.tab;
      renderEscalasView(container, activePatientId);
    });
  });

  // Attach Logic según la pestaña
  if (currentTab === 'braden') initBradenEvents(container, paciente);
  if (currentTab === 'barthel') initBarthelEvents(container, paciente);
  if (currentTab === 'morse') initMorseEvents(container, paciente);
}

// ==========================================
// 1. ESCALA DE BRADEN (RIESGO UPP)
// ==========================================
function renderBradenCalculator() {
  return `
    <div style="display: grid; grid-template-columns: 1fr 340px; gap: 1.5rem; align-items: start;">
      <div class="card">
        <h3 class="card-title" style="margin-bottom: 1rem; color: var(--primary-800);">
          Evaluación de Parámetros - Escala de Braden
        </h3>

        <div class="braden-group form-group">
          <label class="form-label">1. Percepción Sensorial (Capacidad de responder al malestar)</label>
          <select id="braden-sensorial" class="form-control braden-input">
            <option value="1">1 - Completamente limitada (No responde a estímulos dolorosos)</option>
            <option value="2" selected>2 - Muy limitada (Responde solo a estímulos dolorosos)</option>
            <option value="3">3 - Ligeramente limitada (Responde a órdenes verbales, dificultad comunicar dolor)</option>
            <option value="4">4 - Sin limitaciones (Responde a órdenes verbales, siente y expresa dolor)</option>
          </select>
        </div>

        <div class="braden-group form-group">
          <label class="form-label">2. Exposición a la Humedad (Piel expuesta a transpiración u orina)</label>
          <select id="braden-humedad" class="form-control braden-input">
            <option value="1">1 - Constantemente húmeda (Sudoración profusa o incontinencia constante)</option>
            <option value="2" selected>2 - A menudo húmeda (Requiere cambio de sábanas/pañal varias veces)</option>
            <option value="3">3 - Ocasionalmente húmeda (Requiere un cambio de ropa extra al día)</option>
            <option value="4">4 - Raramente húmeda (Piel usualmente seca)</option>
          </select>
        </div>

        <div class="braden-group form-group">
          <label class="form-label">3. Actividad Física (Grado de actividad motora)</label>
          <select id="braden-actividad" class="form-control braden-input">
            <option value="1" selected>1 - Encamado (Confinado permanentemente a la cama)</option>
            <option value="2">2 - En silla (Capacidad de deambular muy limitada, confinado a silla)</option>
            <option value="3">3 - Deambula ocasionalmente (Camina distancias cortas asistido)</option>
            <option value="4">4 - Deambula frecuentemente (Camina fuera de la habitación)</option>
          </select>
        </div>

        <div class="braden-group form-group">
          <label class="form-label">4. Movilidad (Capacidad para cambiar de postura)</label>
          <select id="braden-movilidad" class="form-control braden-input">
            <option value="1">1 - Completamente inmóvil (No hace cambios sin ayuda total)</option>
            <option value="2" selected>2 - Muy limitada (Hace cambios ocasionales y ligeros)</option>
            <option value="3">3 - Ligeramente limitada (Efectúa cambios frecuentes de posición)</option>
            <option value="4">4 - Sin limitaciones (Efectúa cambios importantes sin asistencia)</option>
          </select>
        </div>

        <div class="braden-group form-group">
          <label class="form-label">5. Nutrición (Patrón alimentario diario)</label>
          <select id="braden-nutricion" class="form-control braden-input">
            <option value="1">1 - Muy pobre (Rara vez come una comida completa, ayuno)</option>
            <option value="2">2 - Probablemente inadecuada (Come cerca de la mitad de los alimentos)</option>
            <option value="3" selected>3 - Adecuada (Toma más de la mitad de las comidas, o sonda/PEG bien tolerada)</option>
            <option value="4">4 - Excelente (Come la mayor parte de las comidas, buen aporte proteico)</option>
          </select>
        </div>

        <div class="braden-group form-group">
          <label class="form-label">6. Fricción y Cizallamiento (Fuerza mecánica al mover o incorporarse)</label>
          <select id="braden-friccion" class="form-control braden-input">
            <option value="1">1 - Problema constante (Requiere asistencia máxima, resbala en cama continuamente)</option>
            <option value="2" selected>2 - Problema potencial (Se mueve débilmente, ligera asistencia)</option>
            <option value="3">3 - No existe problema aparente (Se mueve en cama y silla con total control)</option>
          </select>
        </div>
      </div>

      <!-- Panel Lateral de Resultado Braden -->
      <div class="card" style="position: sticky; top: 80px; text-align: center;">
        <h4 style="font-size: 0.9rem; text-transform: uppercase; color: var(--text-muted); font-weight: 700;">
          Puntaje Total Braden
        </h4>
        <div id="braden-total-display" style="font-size: 3.5rem; font-weight: 800; font-family: var(--font-mono); margin: 0.5rem 0; color: #ef4444;">
          12
        </div>
        <div id="braden-badge-risk" class="badge badge-danger" style="font-size: 0.9rem; padding: 0.4rem 0.8rem; margin-bottom: 1rem;">
          Riesgo Alto (12 pts)
        </div>

        <div id="braden-protocol-advice" style="background: var(--slate-50); padding: 0.85rem; border-radius: var(--radius-md); font-size: 0.8rem; text-align: left; margin-bottom: 1.25rem; border: 1px solid var(--border-subtle);">
          <strong>Recomendación Clínica:</strong>
          <ul style="margin-top: 0.3rem; padding-left: 1.2rem; list-style-type: disc;">
            <li>Colchón antiescaras de presión alternante obligatorio.</li>
            <li>Cambios posturales cada 2 a 3 horas.</li>
            <li>Protección de talones y prominencias óseas.</li>
            <li>Hidratación cutánea con ácidos grasos hiperoxigenados.</li>
          </ul>
        </div>

        <button id="btn-guardar-braden" class="btn btn-primary" style="width: 100%;">
          💾 Guardar Valoración en HCD
        </button>
      </div>
    </div>
  `;
}

function initBradenEvents(container, paciente) {
  const inputs = container.querySelectorAll('.braden-input');
  const display = document.getElementById('braden-total-display');
  const badge = document.getElementById('braden-badge-risk');

  function recalculate() {
    let sum = 0;
    inputs.forEach(i => sum += Number(i.value));
    if (display) display.textContent = sum;

    let text = '';
    let color = '#22c55e';
    let badgeClass = 'badge-success';

    if (sum <= 9) {
      text = 'Riesgo Muy Alto (≤ 9 pts)';
      color = '#ef4444';
      badgeClass = 'badge-danger';
    } else if (sum <= 12) {
      text = 'Riesgo Alto (10-12 pts)';
      color = '#ef4444';
      badgeClass = 'badge-danger';
    } else if (sum <= 14) {
      text = 'Riesgo Moderado (13-14 pts)';
      color = '#f59e0b';
      badgeClass = 'badge-warning';
    } else if (sum <= 18) {
      text = 'Riesgo Bajo (15-18 pts)';
      color = '#0ea5e9';
      badgeClass = 'badge-primary';
    } else {
      text = 'Sin Riesgo Significativo (> 18 pts)';
      color = '#22c55e';
      badgeClass = 'badge-success';
    }

    if (display) display.style.color = color;
    if (badge) {
      badge.className = `badge ${badgeClass}`;
      badge.textContent = text;
    }
  }

  inputs.forEach(i => i.addEventListener('change', recalculate));
  recalculate();

  const btnGuardar = document.getElementById('btn-guardar-braden');
  if (btnGuardar) {
    btnGuardar.addEventListener('click', async () => {
      const sum = Number(display.textContent);
      const clasif = badge.textContent;
      const color = display.style.color;

      const escalaData = {
        patientId: paciente.id,
        tipo: 'Braden',
        puntaje: sum,
        clasificacion: clasif,
        color: color,
        detalles: {
          sensorial: Number(document.getElementById('braden-sensorial')?.value || 2),
          humedad: Number(document.getElementById('braden-humedad')?.value || 2),
          actividad: Number(document.getElementById('braden-actividad')?.value || 1),
          movilidad: Number(document.getElementById('braden-movilidad')?.value || 2),
          nutricion: Number(document.getElementById('braden-nutricion')?.value || 3),
          friccion: Number(document.getElementById('braden-friccion')?.value || 2)
        }
      };

      const user = authState.getActiveUser();
      await appsScriptClient.saveEscala(escalaData, user);
      notify.success(`Escala de Braden guardada exitosamente (${sum} pts).`);
      renderEscalasView(container, paciente.id);
    });
  }
}

// ==========================================
// 2. ÍNDICE DE BARTHEL (AVD)
// ==========================================
function renderBarthelCalculator() {
  return `
    <div style="display: grid; grid-template-columns: 1fr 340px; gap: 1.5rem; align-items: start;">
      <div class="card">
        <h3 class="card-title" style="margin-bottom: 1rem; color: var(--teal-700);">
          Evaluación de Actividades de la Vida Diaria (AVD) - Barthel
        </h3>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">1. Comer</label>
            <select id="barthel-comer" class="form-control barthel-input">
              <option value="10">10 - Independiente (Capaz de comer por sí solo)</option>
              <option value="5" selected>5 - Necesita ayuda para cortar o untar</option>
              <option value="0">0 - Dependiente total</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">2. Lavarse / Bañarse</label>
            <select id="barthel-lavarse" class="form-control barthel-input">
              <option value="5">5 - Independiente (Se lava solo en ducha o baño)</option>
              <option value="0" selected>0 - Dependiente (Requiere asistencia)</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">3. Vestirse</label>
            <select id="barthel-vestirse" class="form-control barthel-input">
              <option value="10">10 - Independiente (Se viste y ata cordones)</option>
              <option value="5">5 - Necesita ayuda parcial</option>
              <option value="0" selected>0 - Dependiente total</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">4. Arreglarse / Aseo personal</label>
            <select id="barthel-aseo" class="form-control barthel-input">
              <option value="5">5 - Independiente (Lavado de cara, peinado, dientes)</option>
              <option value="0" selected>0 - Dependiente</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">5. Deposiciones (Continencia Fecal)</label>
            <select id="barthel-deposicion" class="form-control barthel-input">
              <option value="10">10 - Continente (Sin episodios de incontinencia)</option>
              <option value="5" selected>5 - Accidente ocasional (1 vez por semana)</option>
              <option value="0">0 - Incontinente</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">6. Micción (Continencia Urinaria)</label>
            <select id="barthel-miccion" class="form-control barthel-input">
              <option value="10">10 - Continente (Mantiene sequedad día y noche)</option>
              <option value="5" selected>5 - Accidente ocasional o sonda manejada</option>
              <option value="0">0 - Incontinente total o colector no autónomo</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">7. Uso del Retrete</label>
            <select id="barthel-retrete" class="form-control barthel-input">
              <option value="10">10 - Independiente (Entra, sale, se limpia)</option>
              <option value="5">5 - Necesita ayuda para apoyo o ropa</option>
              <option value="0" selected>0 - Dependiente total</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">8. Traslado Cama-Sillón</label>
            <select id="barthel-traslado" class="form-control barthel-input">
              <option value="15">15 - Independiente</option>
              <option value="10">10 - Mínima ayuda o supervisión</option>
              <option value="5" selected>5 - Gran ayuda (Capaz de sentarse con asistencia)</option>
              <option value="0">0 - Dependiente total (No se mantiene sentado)</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">9. Deambulación</label>
            <select id="barthel-deambular" class="form-control barthel-input">
              <option value="15">15 - Independiente al menos 50 metros</option>
              <option value="10">10 - Necesita ayuda o supervisión física</option>
              <option value="5" selected>5 - Independiente en silla de ruedas 50m</option>
              <option value="0">0 - Inmóvil</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">10. Subir y Bajar Escaleras</label>
            <select id="barthel-escaleras" class="form-control barthel-input">
              <option value="10">10 - Independiente</option>
              <option value="5">5 - Necesita ayuda o supervisión</option>
              <option value="0" selected>0 - Incapaz</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Panel Lateral Barthel -->
      <div class="card" style="position: sticky; top: 80px; text-align: center;">
        <h4 style="font-size: 0.9rem; text-transform: uppercase; color: var(--text-muted); font-weight: 700;">
          Puntuación Barthel
        </h4>
        <div id="barthel-total-display" style="font-size: 3.5rem; font-weight: 800; font-family: var(--font-mono); margin: 0.5rem 0; color: #f97316;">
          25
        </div>
        <div id="barthel-badge-dep" class="badge badge-warning" style="font-size: 0.9rem; padding: 0.4rem 0.8rem; margin-bottom: 1rem;">
          Dependencia Grave (25 pts)
        </div>

        <div style="background: var(--slate-50); padding: 0.85rem; border-radius: var(--radius-md); font-size: 0.8rem; text-align: left; margin-bottom: 1.25rem; border: 1px solid var(--border-subtle);">
          <strong>Criterio de Cobertura:</strong>
          <p style="margin-top: 0.3rem;">Puntaje menor a 35 pts justifica módulo de <em>Alta Complejidad</em> con enfermería prolongada y kinesiología motora diaria.</p>
        </div>

        <button id="btn-guardar-barthel" class="btn btn-teal" style="width: 100%;">
          💾 Guardar Índice Barthel en HCD
        </button>
      </div>
    </div>
  `;
}

function initBarthelEvents(container, paciente) {
  const inputs = container.querySelectorAll('.barthel-input');
  const display = document.getElementById('barthel-total-display');
  const badge = document.getElementById('barthel-badge-dep');

  function recalculate() {
    let sum = 0;
    inputs.forEach(i => sum += Number(i.value));
    if (display) display.textContent = sum;

    let text = '';
    let color = '#22c55e';
    let badgeClass = 'badge-success';

    if (sum < 20) {
      text = 'Dependencia Total (< 20 pts)';
      color = '#ef4444';
      badgeClass = 'badge-danger';
    } else if (sum <= 35) {
      text = 'Dependencia Grave (20-35 pts)';
      color = '#f97316';
      badgeClass = 'badge-warning';
    } else if (sum <= 55) {
      text = 'Dependencia Moderada (40-55 pts)';
      color = '#f59e0b';
      badgeClass = 'badge-warning';
    } else if (sum < 100) {
      text = 'Dependencia Leve (≥ 60 pts)';
      color = '#0ea5e9';
      badgeClass = 'badge-primary';
    } else {
      text = 'Independiente (100 pts)';
      color = '#22c55e';
      badgeClass = 'badge-success';
    }

    if (display) display.style.color = color;
    if (badge) {
      badge.className = `badge ${badgeClass}`;
      badge.textContent = text;
    }
  }

  inputs.forEach(i => i.addEventListener('change', recalculate));
  recalculate();

  const btnGuardar = document.getElementById('btn-guardar-barthel');
  if (btnGuardar) {
    btnGuardar.addEventListener('click', async () => {
      const sum = Number(display.textContent);
      const escalaData = {
        patientId: paciente.id,
        tipo: 'Barthel',
        puntaje: sum,
        clasificacion: badge.textContent,
        color: display.style.color,
        detalles: {}
      };

      const user = authState.getActiveUser();
      await appsScriptClient.saveEscala(escalaData, user);
      notify.success(`Índice de Barthel guardado exitosamente (${sum}/100 pts).`);
      renderEscalasView(container, paciente.id);
    });
  }
}

// ==========================================
// 3. ESCALA DE MORSE (RIESGO DE CAÍDAS)
// ==========================================
function renderMorseCalculator() {
  return `
    <div style="display: grid; grid-template-columns: 1fr 340px; gap: 1.5rem; align-items: start;">
      <div class="card">
        <h3 class="card-title" style="margin-bottom: 1rem; color: var(--danger-600);">
          Valoración del Riesgo de Caídas - Escala de Morse
        </h3>

        <div class="form-group">
          <label class="form-label">1. Historial de Caídas (¿Ha tenido caídas en los últimos 3 meses?)</label>
          <select id="morse-caidas" class="form-control morse-input">
            <option value="25" selected>Sí (25 pts)</option>
            <option value="0">No (0 pts)</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">2. Diagnósticos Secundarios (¿Tiene más de un diagnóstico médico activo?)</label>
          <select id="morse-diag" class="form-control morse-input">
            <option value="15" selected>Sí (15 pts)</option>
            <option value="0">No (0 pts)</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">3. Ayuda para Deambular</label>
          <select id="morse-ayuda" class="form-control morse-input">
            <option value="30">Se apoya en muebles / paredes para caminar (30 pts)</option>
            <option value="15" selected>Usa bastón, muletas o andador (15 pts)</option>
            <option value="0">Ninguna / Encamado / Asistencia por enfermero (0 pts)</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">4. Vía Venosa o Catéter Endovenoso Periférico</label>
          <select id="morse-via" class="form-control morse-input">
            <option value="20">Sí, tiene vía venosa conectada o salinizada (20 pts)</option>
            <option value="0" selected>No (0 pts)</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">5. Marcha / Equilibrio</label>
          <select id="morse-marcha" class="form-control morse-input">
            <option value="20">Alterada / Pasos cortos arrastrados (20 pts)</option>
            <option value="10" selected>Débil / Dificultad para levantarse (10 pts)</option>
            <option value="0">Normal o Encamado (0 pts)</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">6. Estado Mental</label>
          <select id="morse-mental" class="form-control morse-input">
            <option value="15">Sobrestima sus capacidades o desorientado (15 pts)</option>
            <option value="0" selected>Orientado en tiempo y espacio / Consciente de sus limitaciones (0 pts)</option>
          </select>
        </div>
      </div>

      <!-- Panel Lateral Morse -->
      <div class="card" style="position: sticky; top: 80px; text-align: center;">
        <h4 style="font-size: 0.9rem; text-transform: uppercase; color: var(--text-muted); font-weight: 700;">
          Riesgo de Caídas Morse
        </h4>
        <div id="morse-total-display" style="font-size: 3.5rem; font-weight: 800; font-family: var(--font-mono); margin: 0.5rem 0; color: #ef4444;">
          65
        </div>
        <div id="morse-badge-risk" class="badge badge-danger" style="font-size: 0.9rem; padding: 0.4rem 0.8rem; margin-bottom: 1rem;">
          Riesgo Alto (≥ 45 pts)
        </div>

        <div style="background: var(--slate-50); padding: 0.85rem; border-radius: var(--radius-md); font-size: 0.8rem; text-align: left; margin-bottom: 1.25rem; border: 1px solid var(--border-subtle);">
          <strong>Plan de Prevención:</strong>
          <ul style="margin-top: 0.3rem; padding-left: 1.2rem; list-style-type: disc;">
            <li>Cama a nivel más bajo con barrales laterales elevados.</li>
            <li>Calzado antideslizante obligatorio.</li>
            <li>Luz de noche permanente en pasillos hacia el baño.</li>
            <li>Acompañamiento asistido en toda transferencia.</li>
          </ul>
        </div>

        <button id="btn-guardar-morse" class="btn btn-danger" style="width: 100%;">
          💾 Guardar Riesgo Caídas en HCD
        </button>
      </div>
    </div>
  `;
}

function initMorseEvents(container, paciente) {
  const inputs = container.querySelectorAll('.morse-input');
  const display = document.getElementById('morse-total-display');
  const badge = document.getElementById('morse-badge-risk');

  function recalculate() {
    let sum = 0;
    inputs.forEach(i => sum += Number(i.value));
    if (display) display.textContent = sum;

    let text = '';
    let color = '#22c55e';
    let badgeClass = 'badge-success';

    if (sum >= 45) {
      text = 'Riesgo Alto de Caída (≥ 45 pts)';
      color = '#ef4444';
      badgeClass = 'badge-danger';
    } else if (sum >= 25) {
      text = 'Riesgo Medio (25-44 pts)';
      color = '#f59e0b';
      badgeClass = 'badge-warning';
    } else {
      text = 'Riesgo Bajo (0-24 pts)';
      color = '#22c55e';
      badgeClass = 'badge-success';
    }

    if (display) display.style.color = color;
    if (badge) {
      badge.className = `badge ${badgeClass}`;
      badge.textContent = text;
    }
  }

  inputs.forEach(i => i.addEventListener('change', recalculate));
  recalculate();

  const btnGuardar = document.getElementById('btn-guardar-morse');
  if (btnGuardar) {
    btnGuardar.addEventListener('click', async () => {
      const sum = Number(display.textContent);
      const escalaData = {
        patientId: paciente.id,
        tipo: 'Morse',
        puntaje: sum,
        clasificacion: badge.textContent,
        color: display.style.color,
        detalles: {}
      };

      const user = authState.getActiveUser();
      await appsScriptClient.saveEscala(escalaData, user);
      notify.success(`Escala de Morse guardada exitosamente (${sum} pts).`);
      renderEscalasView(container, paciente.id);
    });
  }
}
